import { ChangeDetectionStrategy, Component, effect, inject, input, output, viewChild } from '@angular/core';
import type { JuiImageClick, JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import { JuiButton, JuiIcon, JuiImageCapture } from '@jump-tech-frontend/core-components-angular';
import { QuestionExampleImagesComponent } from '../../components/question-example-images.component';
import { FormUpdate } from '../question.model';
import { ImageOrFileQuestionParams } from './image-or-file.model';
import { ImageOrFilePresenter } from './image-or-file.presenter';

@Component({
  selector: 'question-image-or-file',
  templateUrl: './image-or-file.component.html',
  styleUrls: ['./image-or-file.component.scss'],
  imports: [JuiButton, JuiIcon, JuiImageCapture, QuestionExampleImagesComponent],
  providers: [ImageOrFilePresenter],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageOrFileComponent {
  private readonly presenter = inject(ImageOrFilePresenter);
  private readonly control = viewChild.required(JuiImageCapture);

  public readonly params = input.required<ImageOrFileQuestionParams>();
  public readonly formChange = output<FormUpdate>();
  public readonly imageOpen = output<number>();

  public readonly vm = this.presenter.vm;

  constructor() {
    this.presenter.onValueChange(value => this.formChange.emit({ key: this.params().key, value }));
    effect(() => this.presenter.load(this.params()));
  }

  public onImagesChange(event: CustomEvent<JuiImageEntry[]>): void {
    this.presenter.imagesChanged(event.detail);
  }

  public onImageClick(event: CustomEvent<JuiImageClick>): void {
    if (this.vm().readonly) {
      this.imageOpen.emit(event.detail.index);
    }
  }

  public onReject(event: CustomEvent<JuiImageRejection>): void {
    this.presenter.rejected(event.detail);
  }

  public async onDocumentsPicked(input: HTMLInputElement): Promise<void> {
    await this.presenter.documentsPicked(Array.from(input.files ?? []));
    input.value = '';
  }

  public removeDocument(index: number): void {
    this.presenter.removeDocument(index);
  }

  public onCaptureRequest(event: CustomEvent<void>): void {
    if (!this.presenter.offersCamera()) {
      return;
    }
    event.preventDefault();
    void this.captureWithCamera();
  }

  private async captureWithCamera(): Promise<void> {
    const photo = await this.presenter.captureWithCamera(() => void this.control().openPicker());
    if (photo) {
      await this.control().addImages([photo]);
    }
  }
}
